// Every page (layout/theme.liquid). Two kinds of words in page text become links:
//
// - "select your jacket" (any capitals; homepage and product pages only) brings The Fitting's card into view and
//   gives it a brief gold glow, as the card's own button does. It replaced "Check Your Fit" in customer copy on
//   31 Oct 2026 (brand/04_Copy/drafts/check-your-fit-rename.md). On product pages the link carries data-af-story-fit,
//   so assets/af-product.js and af-pdp.js handle it exactly like the models section's button. On the homepage this
//   script scrolls to the page's fitting card (sections/af-home-fitting.liquid); if the page ever loses that section,
//   its links open the homepage at its fitting (#check-your-fit) instead, where this script finishes the move on
//   arrival.
// - "No zipper on your hood, or a different brand?" (every page) opens the enquiry form: the link points at
//   #af-no-zipper, which snippets/af-inquiry-form.liquid listens for. The form is on every page (the product
//   section renders it on product pages, the layout everywhere else).
//
// Text already inside links, buttons, headings, drawer titles, the fitting itself, the header, dialogs and the
// reviews widget is left alone.
//
// It also puts a "Fitted to your {jacket}" pill in the header once a fit is saved (see fittedLabel below).

const CHECK = 'select your jacket';
const NOZIP = 'No zipper on your hood, or a different brand?';
const PATTERN = /(select your jacket|No zipper on your hood, or a different brand\?)/i;
const isCheck = (text) => text.toLowerCase() === CHECK;
const isNoZip = (text) => text.toLowerCase() === NOZIP.toLowerCase();
const SKIP = [
  'a', 'button', 'input', 'textarea', 'select', 'option', 'label', 'script', 'style', 'noscript', 'dialog', 'summary',
  'h1', 'h2', 'h3', '.fur-quiz', '.af-fit-card', '.header', '.header-sidebar', 'af-pdp-bar', '.jdgm-widget',
  '[contenteditable]',
].join(', ');

const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

// "select your jacket" becomes a link on the homepage and product pages (the About page lost its card on 31 Oct 2026)
const page = document.body.classList.contains('af-home') ? 'home'
  : document.body.classList.contains('af-product-page') ? 'product'
  : null;

const fittingCard = () => document.querySelector('[data-af-hfit-card]');

// Points a link at the fitting card, the way each page reaches it
function pointAtFitting(link) {
  if (page === 'product') {
    link.href = '#af-product';
    link.dataset.afStoryFit = '';
  } else if (fittingCard()) {
    link.href = '#check-your-fit';
    link.dataset.afCyf = '';
  } else {
    link.href = `${window.Shopify?.routes?.root || '/'}#check-your-fit`;
  }
  return link;
}

// The link keeps the words as written ("Select your jacket" in a title, "select your jacket" in a sentence)
function checkLink(text) {
  const link = document.createElement('a');
  link.className = 'link af-cyf-link';
  link.textContent = text;
  return pointAtFitting(link);
}

function noZipLink() {
  const link = document.createElement('a');
  link.className = 'link af-nozip-link';
  link.href = '#af-no-zipper';
  link.textContent = NOZIP;
  return link;
}

function linkWords(scope = document.body) {
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const text = node.nodeValue.toLowerCase();
      const wanted = text.includes(NOZIP.toLowerCase()) || (page && text.includes(CHECK));
      if (!wanted) return NodeFilter.FILTER_REJECT;
      return node.parentElement?.closest(SKIP) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    const parts = node.nodeValue.split(PATTERN);
    const fragment = document.createDocumentFragment();
    parts.forEach((part) => {
      if (isNoZip(part)) fragment.append(noZipLink());
      else if (isCheck(part) && page) fragment.append(checkLink(part));
      else if (part) fragment.append(part);
    });
    node.replaceWith(fragment);
  });
}

// Waits until the page has stopped scrolling (or 3s), then runs the callback
function afterSettle(callback) {
  const started = performance.now();
  let lastY = scrollY;
  let still = 0;
  const tick = () => {
    still = Math.abs(scrollY - lastY) < 1 ? still + 1 : 0;
    lastY = scrollY;
    if (still >= 8 || performance.now() - started > 3000) {
      callback();
      return;
    }
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// The card's "answer this first" glow: gold, rising around the card and fading in about 1s (af-pdp.css .af-glow,
// the same as assets/af-pdp.js; it replaced a sway on 31 Oct 2026)
// A glow already showing plays to its end; presses during it don't restart it
function glow(card) {
  if (!card || card.classList.contains('af-glow')) return;
  card.classList.add('af-glow');
  setTimeout(() => card.classList.remove('af-glow'), 1050);
}

// Homepage and About: the fitting card into the middle of the screen (or its top, if it's taller than the screen),
// then the glow, unless the fit is already confirmed
function goToFitting() {
  const card = fittingCard();
  if (!card) return false;
  document.dispatchEvent(new CustomEvent('arcticfur:check-fit', { detail: {} }));
  requestAnimationFrame(() => {
    // The header sticks on these pages, so the card goes in the space under it
    const header = document.querySelector('.shopify-section--header');
    const sticky = header && getComputedStyle(header).position === 'sticky' ? header.offsetHeight : 0;
    const rect = card.getBoundingClientRect();
    const room = innerHeight - sticky;
    const offset = rect.height < room - 32 ? sticky + (room - rect.height) / 2 : sticky + 16;
    scrollTo({ top: rect.top + scrollY - offset, behavior: reducedMotion() ? 'auto' : 'smooth' });
    afterSettle(() => {
      if (card.querySelector('.fur-quiz')?.dataset.afFitReady !== 'true') glow(card);
    });
  });
  return true;
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[data-af-cyf]');
  if (!link) return;
  if (goToFitting()) event.preventDefault();
});

// The strip under the hero: its "Select your jacket" item (icon, title and line) is one link to the card
function linkStripItem(scope = document) {
  scope.querySelectorAll('.text-with-icons__item').forEach((item) => {
    const link = item.querySelector('.af-cyf-link');
    if (!link || item.classList.contains('af-cyf-item')) return;
    item.classList.add('af-cyf-item');
    item.addEventListener('click', (event) => {
      if (!event.target.closest('a')) link.click();
    });
  });
}

linkWords();
linkStripItem();
document.addEventListener('shopify:section:load', (event) => {
  linkWords(event.target);
  linkStripItem(event.target);
});

// "Fitted to your {jacket}" in the header, from the fitting's own memory (arcticfur:quiz-fit:v1, 30 days): a small
// pill with a green tick beside the currency picker on desktop, beside the bag on phones (just "Fitted" there). It
// pops in only when a fit is confirmed on this page; on later pages it's simply there, and it goes when the fit is
// cleared. It isn't a link: hovering it on desktop (or focusing it) shows a small card explaining the saved fit;
// on phones a tap opens the card and a second tap, a tap anywhere else, Esc or scrolling closes it.
// A fit made to a zipper length the customer typed (the Expedition, an unlisted model, other brands) names that
// length ("Fitted for 55 cm zipper"); every other fit names the jacket. The fitting's own lengths are never shown.
const FIT_KEY = 'arcticfur:quiz-fit:v1';
const FITTED_TITLE = 'Your fit is saved.';
// The last two words are joined by a no-break space, so "page." never sits alone on the card's last line
const FITTED_END = 'so you can go straight to checkout. To change it, select Restart on any product\u00A0page.';

function savedFit() {
  try {
    const saved = JSON.parse(localStorage.getItem(FIT_KEY));
    if (!saved || !saved.base || !saved.zipperType || !saved.zipperLength) return null;
    if (Date.now() - (saved.savedAt || 0) > 30 * 24 * 60 * 60 * 1000) return null;
    if (saved.needsLength) {
      const length = String(saved.zipperLength).replace(/[^\d.]/g, '');
      if (length) return { key: `len:${length}`, label: `Fitted for ${length} cm zipper`, line: `Every trim is made to your ${length} cm zipper measurement, ${FITTED_END}` };
    }
    const base = String(saved.base);
    let model = /^(new season|heritage)/i.test(base) ? base.replace(/^(new season|heritage)\s*-?\s*/i, '') : '';
    model = model.replace(/\s*-?\s*\d+(\.\d+)?\s*cm$/i, '').trim();
    if (/^other$/i.test(model)) model = '';
    const name = model || 'jacket';
    return { key: `model:${name}`, label: `Fitted to your ${name}`, line: `Every trim is matched to your ${name}, ${FITTED_END}` };
  } catch {
    return null;
  }
}

const touchOnly = () => !matchMedia('(hover: hover) and (pointer: fine)').matches;
let fitted = null;
let fittedKey = '';
let scrollAtOpen = 0;

function closeFittedNote() {
  if (!fitted?.classList.contains('is-open')) return;
  fitted.classList.remove('is-open');
  fitted.querySelector('.af-fitted__pill').setAttribute('aria-expanded', 'false');
}

function fittedLabel() {
  const nav = document.querySelector('.header__secondary-nav');
  if (!nav) return;
  fitted?.remove();
  fitted = document.createElement('div');
  fitted.className = 'af-fitted';
  fitted.hidden = true;
  fitted.innerHTML = '<button type="button" class="af-fitted__pill" aria-expanded="false" aria-controls="af-fitted-note" aria-describedby="af-fitted-note">'
    + '<span class="af-fitted__check" aria-hidden="true"><svg viewBox="0 0 16 16" focusable="false"><path d="m2.5 8.6 3.6 3.6 7.4-8" pathLength="1" fill="none" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>'
    + '<span class="af-fitted__text"><span class="af-fitted__long"></span><span class="af-fitted__short" aria-hidden="true">Fitted</span></span>'
    + '</button>'
    + `<div class="af-fitted__note" id="af-fitted-note"><p class="af-fitted__title">${FITTED_TITLE}</p><p class="af-fitted__line"></p></div>`;
  nav.prepend(fitted);
  const pill = fitted.querySelector('.af-fitted__pill');
  // With a mouse the card shows on hover and focus (CSS), so a click does nothing; on touch screens a tap toggles it
  pill.addEventListener('click', (event) => {
    event.preventDefault();
    if (!touchOnly()) return;
    const open = !fitted.classList.contains('is-open');
    fitted.classList.toggle('is-open', open);
    pill.setAttribute('aria-expanded', String(open));
    scrollAtOpen = scrollY;
  });
  fitted.querySelector('.af-fitted__note').addEventListener('click', closeFittedNote);
  // Esc hides the hover or focus card too, until the pointer leaves or focus moves on
  const unquiet = () => fitted.classList.remove('is-quiet');
  fitted.addEventListener('mouseleave', unquiet);
  pill.addEventListener('blur', unquiet);
  fittedKey = '';
  showFitted();
}

document.addEventListener('pointerdown', (event) => {
  if (fitted && !fitted.contains(event.target)) closeFittedNote();
}, { passive: true });
addEventListener('scroll', () => {
  if (Math.abs(scrollY - scrollAtOpen) > 12) closeFittedNote();
}, { passive: true });
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !fitted || fitted.hidden) return;
  closeFittedNote();
  if (fitted.matches(':hover') || fitted.contains(document.activeElement)) fitted.classList.add('is-quiet');
});

function showFitted({ animate = false } = {}) {
  if (!fitted) return;
  const fit = savedFit();
  if (!fit) {
    closeFittedNote();
    fitted.hidden = true;
    fittedKey = '';
    return;
  }
  fitted.querySelector('.af-fitted__long').textContent = fit.label;
  fitted.querySelector('.af-fitted__line').textContent = fit.line;
  fitted.querySelector('.af-fitted__pill').setAttribute('aria-label', fit.label);
  const isNew = fit.key !== fittedKey;
  fitted.hidden = false;
  fittedKey = fit.key;
  if (animate && isNew) {
    fitted.classList.remove('is-new');
    void fitted.offsetWidth;
    fitted.classList.add('is-new');
  }
}

fittedLabel();
// The fitting saves before it announces the change, but read on the next tick to be safe
document.addEventListener('arcticfur:fit-change', (event) => {
  const confirmed = !!event.detail?.complete && !event.detail?.fitCheck;
  setTimeout(() => showFitted({ animate: confirmed }), 0);
});
addEventListener('storage', (event) => {
  if (event.key === FIT_KEY || event.key === null) showFitted();
});
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.header__secondary-nav')) fittedLabel();
});

// "Check my fit" eases back to gold after a click even while the pointer is still on it (af-pdp.css), until the
// pointer leaves; a fresh hover then fades it clear again
document.addEventListener('click', (event) => {
  const button = event.target.closest?.('.af-fit-card .af-main-fit');
  if (!button) return;
  button.setAttribute('data-af-rest', '');
  button.addEventListener('pointerleave', () => button.removeAttribute('data-af-rest'), { once: true });
}, true);

// Arriving from a link to the homepage's fitting
if (location.hash === '#check-your-fit' && fittingCard()) {
  const arrive = () => setTimeout(goToFitting, 150);
  if (document.readyState === 'complete') arrive();
  else addEventListener('load', arrive, { once: true });
}

// The announcement bar's close button (sections/announcement-bar.liquid). The bar folds away, the full-screen heroes
// growing with it (they size themselves by --announcement-bar-height), and it stays closed on every page until its
// message changes: the button carries a fingerprint of the message, and the section hides the bar before it's drawn
// when that fingerprint is the one saved here. No fold with Reduce Motion.
const ANNOUNCE_KEY = 'arcticfur:announcement-closed:v1';

function closeAnnouncement(button) {
  const section = button.closest('.shopify-section');
  const root = document.documentElement;
  try {
    localStorage.setItem(ANNOUNCE_KEY, button.dataset.afAnnounceClose);
  } catch {}
  if (button.matches(':focus-visible')) document.querySelector('.header a[href]')?.focus();
  const finish = () => {
    root.classList.add('af-announce-closed');
    section?.style.removeProperty('height');
    section?.style.removeProperty('overflow');
    root.style.setProperty('--announcement-bar-height', '0px');
  };
  const start = section?.offsetHeight || 0;
  if (!start || matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finish();
    return;
  }
  section.style.overflow = 'hidden';
  const began = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - began) / 260);
    const height = start * (1 - t) ** 3;
    section.style.height = `${height}px`;
    root.style.setProperty('--announcement-bar-height', `${height}px`);
    if (t < 1) requestAnimationFrame(step);
    else finish();
  };
  requestAnimationFrame(step);
}

document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-af-announce-close]');
  if (button) closeAnnouncement(button);
});

// Videos written into a guide's own text (body.af-guide; the attach guide's how-to clip) take the house video style:
// rounded, muted, looping, no browser controls, playing only while on screen (never with Reduce Motion, which shows
// the still frame), with a small play/pause button in the corner. The page text is shared with the live theme, so
// it isn't edited: the attach guide's clip is swapped here for the theme's lighter copy (assets/af-attach-trim.mp4:
// H.264, 13 MB instead of 39 MB, no sound track, same 1080 x 1920 at 60 fps; the original is HEVC, which Chrome on
// many Android phones and Windows PCs can't play) and given its still frame (af-attach-trim-poster.webp).
const LIGHTER_VIDEOS = {
  '944a7151bce04fb5833b9582d4bab25f': { src: 'af-attach-trim.mp4', poster: 'af-attach-trim-poster.webp' },
};
const PLAY_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M5 3.2v9.6L12.6 8z" fill="currentColor"/></svg>';
const PAUSE_ICON = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M4.5 3h2.4v10H4.5zm4.6 0h2.4v10H9.1z" fill="currentColor"/></svg>';

function guideVideo(video) {
  if (video.dataset.afVideo) return;
  video.dataset.afVideo = 'true';
  const source = video.querySelector('source');
  const original = source?.getAttribute('src') || video.getAttribute('src') || '';
  const lighter = Object.entries(LIGHTER_VIDEOS).find(([key]) => original.includes(key))?.[1];
  // The written width and height give the shape until the video's own size is known
  const ratio = parseFloat(video.style.width) / parseFloat(video.style.height);
  video.removeAttribute('style');
  if (ratio > 0) video.style.aspectRatio = String(ratio);
  video.removeAttribute('controls');
  video.controls = false;
  video.muted = true;
  video.defaultMuted = true;
  video.loop = true;
  video.playsInline = true;
  video.preload = 'none';
  if (lighter) {
    video.poster = new URL(lighter.poster, import.meta.url).href;
    if (source) source.src = new URL(lighter.src, import.meta.url).href;
    else video.src = new URL(lighter.src, import.meta.url).href;
    video.load();
  }

  const frame = document.createElement('div');
  frame.className = 'af-video';
  video.before(frame);
  frame.append(video);
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'af-video__toggle';
  frame.append(toggle);
  const paint = () => {
    const playing = !video.paused;
    toggle.innerHTML = playing ? PAUSE_ICON : PLAY_ICON;
    toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
  };
  video.addEventListener('play', paint);
  video.addEventListener('pause', paint);
  paint();

  let stopped = matchMedia('(prefers-reduced-motion: reduce)').matches;
  let visible = false;
  toggle.addEventListener('click', () => {
    if (video.paused) {
      stopped = false;
      video.play().catch(() => {});
    } else {
      stopped = true;
      video.pause();
    }
  });
  // Loads a screen ahead; plays while half of it is on screen, unless the visitor paused it
  new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting && video.preload === 'none') video.preload = 'auto';
  }, { rootMargin: '100% 0px' }).observe(frame);
  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    if (visible && !stopped) video.play().catch(() => {});
    else if (!visible) video.pause();
  }, { threshold: 0.5 }).observe(frame);
}

if (document.body.classList.contains('af-guide')) document.querySelectorAll('.prose video').forEach(guideVideo);

// Background videos (31 Oct 2026): every decorative video (data-af-video: the collection and About heroes, the
// homepage's Showcase and fitting background) uses this one player, in place of each section's own. Muted, looping, no
// controls: it plays while any of it is on screen and pauses once it has gone, loading from a screen away (the heroes
// at the top start straight away, with the autoplay attribute). With Reduce Motion it stays on its still frame. When the
// back button brings a page back, or the tab comes back to the front, the videos on screen play again: Safari restores
// a page with its videos paused, which left a collection's hero frozen after going back. A video hidden by CSS never
// counts as on screen, so it neither loads nor plays.
const videosOnScreen = new Set();
const videoRechecks = new WeakMap();

// Plays a video that's on screen. If the attempt is interrupted (Safari may let go of a video that scrolled away and
// load it again as it returns, which cancels a play() made in between), it tries again once the video can play, and
// once more a second later if something stopped it in the meantime; a video left on screen never sits frozen.
function playVideo(video) {
  if (reducedMotion()) return;
  const retry = () => {
    if (videosOnScreen.has(video) && video.paused && !reducedMotion()) video.play()?.catch(() => {});
  };
  video.muted = true;
  video.play()?.catch(() => video.addEventListener('canplay', retry, { once: true }));
  clearTimeout(videoRechecks.get(video));
  videoRechecks.set(video, setTimeout(retry, 1000));
}

const videoSeen = new IntersectionObserver((entries) => {
  entries.forEach(({ target: video, isIntersecting }) => {
    if (isIntersecting) {
      videosOnScreen.add(video);
      playVideo(video);
    } else {
      videosOnScreen.delete(video);
      video.pause();
    }
  });
});

const videoNear = new IntersectionObserver((entries) => {
  entries.forEach(({ target: video, isIntersecting }) => {
    if (!isIntersecting) return;
    if (video.preload === 'none') video.preload = 'auto';
    videoNear.unobserve(video);
  });
}, { rootMargin: '100% 0px' });

function setUpBackgroundVideo(video) {
  if (video.hasAttribute('data-af-video-ready')) return;
  video.setAttribute('data-af-video-ready', '');
  video.muted = true;
  video.defaultMuted = true;
  video.loop = true;
  video.playsInline = true;
  if (reducedMotion()) {
    video.autoplay = false;
    video.pause();
    return;
  }
  videoNear.observe(video);
  videoSeen.observe(video);
}

const setUpBackgroundVideos = (scope = document) => scope.querySelectorAll('video[data-af-video]').forEach(setUpBackgroundVideo);
setUpBackgroundVideos();
document.addEventListener('shopify:section:load', (event) => setUpBackgroundVideos(event.target));
addEventListener('pageshow', (event) => {
  if (event.persisted) videosOnScreen.forEach(playVideo);
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') videosOnScreen.forEach(playVideo);
});

// The bag's express checkout (sections/cart-drawer.liquid): the wallet button arrives in a template and goes in with
// only the wallet this device shows, Apple Pay where it's available, otherwise Google Pay. With both in its list,
// Shopify's element kept room for the one that never appears, which showed as a gap above the button (31 Oct 2026).
// The bag is redrawn on every change; each new copy fills itself in.
class AfBagExpress extends HTMLElement {
  connectedCallback() {
    const template = this.querySelector(':scope > template');
    if (!template) return;
    const content = template.content.cloneNode(true);
    const wallets = content.querySelector('shopify-accelerated-checkout-cart');
    let apple = false;
    try {
      apple = !!window.ApplePaySession && window.ApplePaySession.canMakePayments();
    } catch {
      apple = false;
    }
    if (wallets) {
      try {
        const configs = JSON.parse(wallets.getAttribute('wallet-configs'));
        const kept = configs.filter((config) => config.name === (apple ? 'apple_pay' : 'google_pay'));
        if (kept.length) wallets.setAttribute('wallet-configs', JSON.stringify(kept));
      } catch {
        // Anything unexpected: the list as Shopify sent it
      }
    }
    template.replaceWith(content);
  }
}
if (!customElements.get('af-bag-express')) customElements.define('af-bag-express', AfBagExpress);

// Live chat (Tidio) on every page. Its app embed is off and the loader is added here instead, once the page has loaded
// and the browser is idle, or at the first tap, scroll or key press, whichever comes first, so the chat (about 520 KB)
// never competes with the page's own photos (31 Oct 2026). Product pages move and hide its button (af-pdp.js). To go
// back, turn the Tidio app embed on again in the theme editor (App embeds) and delete this block.
const TIDIO_LOADER = 'https://code.tidio.co/chw9grr9rcsmbfurqrb0qtsj1z5uzcoj.js';
(() => {
  if (window.tidioChatApi || document.querySelector('script[src*="code.tidio.co"]')) return;
  let done = false;
  const events = ['pointerdown', 'scroll', 'keydown'];
  const load = () => {
    if (done) return;
    done = true;
    events.forEach((type) => removeEventListener(type, load));
    const script = document.createElement('script');
    script.src = TIDIO_LOADER;
    script.async = true;
    document.body.append(script);
  };
  events.forEach((type) => addEventListener(type, load, { once: true, passive: true }));
  // Just the round button while the chat is closed, on every page: no "Chat with us" bubble or message previews
  // beside it (31 Oct 2026; it was product pages only)
  const buttonOnly = () => {
    try {
      window.tidioChatApi?.adjustStyles?.('#tidio-chat-root:has(#button.chat-closed) > :not(#button), #button > :not(#button-body) { display: none !important; }');
    } catch {
      // An older widget without adjustStyles keeps its bubble
    }
  };
  if (window.tidioChatApi) buttonOnly();
  else document.addEventListener('tidioChat-ready', buttonOnly, { once: true });
  const whenIdle = () => (window.requestIdleCallback ? requestIdleCallback(load, { timeout: 3000 }) : setTimeout(load, 1500));
  if (document.readyState === 'complete') whenIdle();
  else addEventListener('load', whenIdle, { once: true });
})();
